import { Alert, Checkbox, DatePicker, Form, Input, InputNumber, Modal, Select, message } from "antd";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createPromoCode, listPromoPlans, promoErrorMessage } from "../../actions/promo_actions";
import { describeDiscount } from "../../utils/promo";

const PERIOD_OPTIONS = [
  { label: "Monthly", value: "MONTHLY" },
  { label: "Yearly", value: "ANNUALLY" },
];

/** Create a promo code for the sales team. Discount terms cannot be changed afterwards. */
export default function PromoCodeFormModal({ open, onClose, onCreated }) {
  const [form] = Form.useForm();
  const percentOff = Form.useWatch("percentOff", form);
  const durationCycles = Form.useWatch("durationCycles", form);
  const billingPeriods = Form.useWatch("billingPeriods", form);
  const { data: plans = [], isLoading: plansLoading } = useQuery({
    queryKey: ["promo-plans"],
    queryFn: listPromoPlans,
    enabled: open,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: createPromoCode,
    onSuccess: (promo) => {
      message.success(`Promo code ${promo.code} created`);
      form.resetFields();
      onCreated?.(promo);
      onClose();
    },
  });

  const submit = (values) => {
    const [validFrom, expiresAt] = values.validity || [];
    mutation.mutate({
      ...(values.code ? { code: values.code.trim().toUpperCase() } : {}),
      percentOff: values.percentOff,
      durationCycles: values.durationCycles,
      billingPeriods: values.billingPeriods,
      ...(values.planIds?.length ? { planIds: values.planIds } : {}),
      ...(values.maxRedemptions ? { maxRedemptions: values.maxRedemptions } : {}),
      ...(validFrom ? { validFrom: validFrom.startOf("day").toISOString() } : {}),
      ...(expiresAt ? { expiresAt: expiresAt.endOf("day").toISOString() } : {}),
      ...(values.description ? { description: values.description } : {}),
    });
  };

  const preview =
    percentOff && durationCycles && billingPeriods?.length
      ? describeDiscount({ percent_off: percentOff, duration_cycles: durationCycles, billing_periods: billingPeriods })
      : null;

  return (
    <Modal
      open={open}
      title="New promo code"
      okText="Create code"
      onOk={() => form.submit()}
      confirmLoading={mutation.isPending}
      onCancel={() => {
        mutation.reset();
        onClose();
      }}
      destroyOnClose
      width={560}
      className="promo-form-modal"
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark="optional"
        initialValues={{ durationCycles: 3, billingPeriods: ["MONTHLY", "ANNUALLY"] }}
        onFinish={submit}
        className="promo-form"
      >
        <Form.Item
          name="code"
          label="Code"
          extra="Leave empty to generate one (for example MS-7K2QX9PD)."
          normalize={(value) => (value || "").toUpperCase()}
          rules={[{ pattern: /^[A-Z0-9-]{4,32}$/, message: "4–32 characters: letters, digits or -" }]}
        >
          <Input placeholder="RAMADAN-20" maxLength={32} />
        </Form.Item>

        <div className="promo-form__row">
          <Form.Item
            name="percentOff"
            label="Discount"
            rules={[{ required: true, message: "Enter a percentage" }]}
          >
            <InputNumber min={1} max={100} precision={2} addonAfter="%" placeholder="20" />
          </Form.Item>
          <Form.Item
            name="durationCycles"
            label="For how many payments"
            tooltip="Counts the vendor's billing periods, including the signup payment: 3 on a monthly plan = the first 3 months."
            rules={[{ required: true, message: "Enter a number of payments" }]}
          >
            <InputNumber min={1} max={120} precision={0} />
          </Form.Item>
        </div>

        <Form.Item
          name="billingPeriods"
          label="Valid for"
          rules={[{ required: true, type: "array", min: 1, message: "Pick at least one billing period" }]}
        >
          <Checkbox.Group options={PERIOD_OPTIONS} />
        </Form.Item>

        <Form.Item name="planIds" label="Plans" extra="Leave empty for every plan.">
          <Select
            mode="multiple"
            allowClear
            loading={plansLoading}
            placeholder="All plans"
            options={plans.map((plan) => ({ label: plan.name, value: plan.id }))}
          />
        </Form.Item>

        {preview && <Alert className="promo-form__preview" type="info" showIcon message={preview} description="Postage and one-time extras are never discounted." />}

        <div className="promo-form__row">
          <Form.Item name="maxRedemptions" label="Usage limit" extra="Empty = unlimited.">
            <InputNumber min={1} precision={0} placeholder="50" />
          </Form.Item>
          <Form.Item name="validity" label="Valid between" extra="Empty = no start or end date.">
            <DatePicker.RangePicker allowEmpty={[true, true]} />
          </Form.Item>
        </div>

        <Form.Item name="description" label="Internal note">
          <Input.TextArea rows={2} maxLength={500} placeholder="Who this is for, campaign name…" />
        </Form.Item>

        {mutation.isError && (
          <Alert type="error" showIcon message="Could not create the code" description={promoErrorMessage(mutation.error)} />
        )}
      </Form>
    </Modal>
  );
}
